'use client';

import type { InputProps } from '../input';

import { forwardRef, useRef, useState } from 'react';

import { useCursor } from '@paid-ui/hooks/use-cursor';
import { useIsomorphicEffect } from '@paid-ui/hooks/use-isomorphic-effect';
import { useMergedRef } from '@paid-ui/hooks/use-merged-ref';
import { formatACN } from '@paid-ui/utils/acn';

import { Input } from '../input';

const MAX_LENGTH = 11;
const INPUT_REGEXP = /^[\d\s]{0,11}$/;
const REPLACE_REGEXP = /\D+/g;
const cleanValue = (value: string) => value.replaceAll(REPLACE_REGEXP, '').slice(0, 9);

export interface ACNInputProps
  extends Omit<
    InputProps,
    'type' | 'inputMode' | 'maxLength' | 'autoComplete' | 'autoCapitalize' | 'autoCorrect'
  > {
  value?: string;
  defaultValue?: string;
}

export const ACNInput = forwardRef<HTMLInputElement, ACNInputProps>(
  ({ value, defaultValue, onChange, onFocus, onBlur, onPaste, ...restProps }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [focus, setFocus] = useState(false);
    const [recordCursor, restoreCursor] = useCursor(inputRef.current, focus);
    const [displayValue, setDisplayValue] = useState(() => formatACN(value ?? defaultValue) ?? '');

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
      setFocus(true);
      const targetValue = cleanValue(event.currentTarget.value);
      event.currentTarget.value = targetValue;
      onFocus?.(event);
      setDisplayValue(formatACN(targetValue) ?? '');
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
      setFocus(false);
      const targetValue = cleanValue(event.currentTarget.value);
      event.currentTarget.value = targetValue;
      onBlur?.(event);
      setDisplayValue(formatACN(targetValue) ?? '');
    };

    const handlePaste: React.ClipboardEventHandler<HTMLInputElement> = (event) => {
      const pastedValue = event.clipboardData.getData('text/plain');
      const targetValue = cleanValue(pastedValue);
      event.currentTarget.value = targetValue;
      onPaste?.(event);
      setDisplayValue(formatACN(targetValue) ?? '');
      // @ts-expect-error - Event type is not compatible.
      onChange?.(event);
      event.preventDefault();
    };

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      // 1. Record the cursor position and related values.
      recordCursor();

      // 2. Get cleaned input value and formatted display value.
      const targetValue = cleanValue(event.currentTarget.value);
      const newDisplayValue = formatACN(targetValue) ?? '';

      // 3. If the input is empty, set the display value to empty and call onChange.
      if (!targetValue) {
        setDisplayValue('');
        onChange?.(event);
        return;
      }

      // 4. If the input is invalid, do nothing.
      // 5. If th new display value is the same as the old one, do nothing.
      if (!INPUT_REGEXP.test(targetValue) || newDisplayValue === displayValue) {
        return;
      }

      // 6. Set the display value to the new one and call onChange.
      setDisplayValue(newDisplayValue);
      event.currentTarget.value = targetValue;
      onChange?.(event);
    };

    useIsomorphicEffect(() => {
      // 7. Restore the cursor position if the display value has changed.
      restoreCursor();
    }, [displayValue]);

    return (
      <Input
        ref={useMergedRef(inputRef, ref)}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onPaste={handlePaste}
        {...restProps}
        type="text"
        inputMode="numeric"
        maxLength={MAX_LENGTH}
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
      />
    );
  },
);

ACNInput.displayName = 'ACNInput';
